import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

/**
 * <bigens-stage-sync> keeps launch-stage UI in step with the selected variant (T11).
 *
 * Horizon only re-renders a few PDP parts on a variant change (price, add-to-cart button, sticky bar).
 * Stage-dependent markup (waitlist form, pre-order note, the `_preorder` cart properties, the
 * estimated-arrival line) lives elsewhere, so wrap it in this element:
 *
 *   <bigens-stage-sync data-sync-id="unique-per-page" data-sync-key="{{ stage }}|{{ ship_date }}">…</bigens-stage-sync>
 *
 * When a variant is selected, Horizon fetches the section HTML for that variant. If the matching element
 * in that HTML has a different `data-sync-key`, its children replace ours. Same key = no DOM work, so a
 * half-typed email or an open form isn't reset when the shopper only changes colour within one stage.
 */
class BigensStageSync extends HTMLElement {
  /** @type {Element | null} */
  #scope = null;

  connectedCallback() {
    this.#scope = this.closest('.shopify-section, dialog');
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#scope = null;
  }

  /** @param {Event & {promise?: Promise<{detail?: {html?: Document | Element}}>}} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        const html = detail?.html;
        if (!html || !this.isConnected) return;

        const selector = `bigens-stage-sync[data-sync-id="${CSS.escape(this.dataset.syncId ?? '')}"]`;
        const next = html.querySelector(selector);
        if (!(next instanceof HTMLElement) || next.dataset.syncKey === this.dataset.syncKey) return;

        morph(this, next, { childrenOnly: true });
        this.dataset.syncKey = next.dataset.syncKey ?? '';
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bigens-stage-sync] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('bigens-stage-sync')) {
  customElements.define('bigens-stage-sync', BigensStageSync);
}
